/**
 * Hook to manage username setup flow
 * 
 * Checks if connected wallet has a username set,
 * and provides methods to set/update username.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { supabase } from '@/integrations/supabase/client';
import { normalizeUsername } from '@/lib/validation/username';
import bs58 from 'bs58';
import { debug } from "@/lib/debug";

interface UsernameSetupState {
  isLoading: boolean;
  needsUsername: boolean;
  currentUsername: string | null;
  usernameUpdatedAt: string | null;
  showSetupModal: boolean;
}

function withTimeout<T>(promise: Promise<T>, ms: number, timeoutMessage: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(timeoutMessage)), ms);
  });

  return Promise.race([
    promise.finally(() => {
      if (timer) clearTimeout(timer);
    }),
    timeoutPromise,
  ]);
}

export function useUsernameSetup() {
  const { publicKey, connected, signMessage, walletAddress } = useTurnkeyWallet();
  const [state, setState] = useState<UsernameSetupState>({
    isLoading: true,
    needsUsername: false,
    currentUsername: null,
    usernameUpdatedAt: null,
    showSetupModal: false,
  });

  // Check if user has username when wallet connects
  useEffect(() => {
    const checkUsername = async () => {
      if (!connected || !walletAddress) {
        setState({
          isLoading: false,
          needsUsername: false,
          currentUsername: null,
          usernameUpdatedAt: null,
          showSetupModal: false,
        });
        return;
      }

      setState(prev => ({ ...prev, isLoading: true }));

      try {
        const { data, error } = await supabase
          .from('creator_profiles')
          .select('username, username_lower, username_updated_at')
          .eq('wallet_address', walletAddress)
          .maybeSingle();

        if (error) {
          console.error('[useUsernameSetup] Error checking username:', error);
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        const hasUsername = !!data?.username;
        
        setState({
          isLoading: false,
          needsUsername: !hasUsername,
          currentUsername: data?.username || null,
          usernameUpdatedAt: data?.username_updated_at || null,
          showSetupModal: !hasUsername, // Show modal if no username
        });
      } catch (err) {
        console.error('[useUsernameSetup] Error:', err);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkUsername();
  }, [connected, walletAddress]);

  // Save username (requires wallet signature)
  const saveUsername = useCallback(async (username: string): Promise<{ success: boolean; error?: string }> => {
    if (!walletAddress || !publicKey) {
      return { success: false, error: 'Wallet not connected' };
    }
    
    if (!signMessage) {
      return { success: false, error: 'Message signing not available. Please reconnect your wallet.' };
    }

    const normalized = normalizeUsername(username);

    try {
      // Step 1: Get challenge
      debug.trace('[useUsernameSetup] Requesting username challenge...');
      const {
        data: challengeData,
        error: challengeError,
        response: challengeResponse,
      } = await withTimeout(
        supabase.functions.invoke('creator-profile-challenge', {
          body: { wallet_address: walletAddress },
        }),
        15_000,
        'Challenge request timed out. Please try again.'
      );

      // supabase-js returns non-2xx as FunctionsHttpError with a Response in `response`
      let challengePayload: any = challengeData;
      if (challengeError && challengeResponse) {
        try {
          const contentType = challengeResponse.headers.get('Content-Type') || '';
          if (contentType.includes('application/json')) {
            challengePayload = await challengeResponse.clone().json();
          }
        } catch {
          // ignore parse errors
        }

        const retryAfterHeader = challengeResponse.headers.get('Retry-After');
        const retryAfterFromHeader = retryAfterHeader ? Number(retryAfterHeader) : undefined;
        const retryAfterFromBody =
          typeof challengePayload?.retry_after === 'number' ? challengePayload.retry_after : undefined;
        const retryAfter =
          (retryAfterFromBody && retryAfterFromBody > 0 ? retryAfterFromBody : undefined) ??
          (retryAfterFromHeader && retryAfterFromHeader > 0 ? retryAfterFromHeader : undefined);

        console.error('[useUsernameSetup] Challenge error:', challengeError);

        if (retryAfter) {
          return { success: false, error: `Please wait ${retryAfter}s and try again.` };
        }

        return {
          success: false,
          error: challengePayload?.error || 'Failed to get challenge. Please try again.',
        };
      }

      if (challengePayload?.error) {
        const retryAfter = challengePayload?.retry_after;
        if (typeof retryAfter === 'number' && retryAfter > 0) {
          return { success: false, error: `Please wait ${retryAfter}s and try again.` };
        }
        return {
          success: false,
          error: challengePayload?.error || 'Failed to get challenge. Please try again.',
        };
      }

      const { message, nonce, expires } = challengePayload;

      // Step 2: Sign message using Turnkey
      let signature: string;
      try {
          const messageBytes = new TextEncoder().encode(message);
          debug.trace('[useUsernameSetup] Awaiting signature approval...');
          const signatureBytes = await withTimeout(
            signMessage(messageBytes),
            60_000,
            'Signature timed out. If you do not see a signing prompt, try again.'
          );
          signature = bs58.encode(signatureBytes);
      } catch (signError) {
        console.error('[useUsernameSetup] Signature rejected:', signError);
        // Show the actual error message instead of generic "rejected"
        const errorMessage = signError instanceof Error ? signError.message : 'Signature rejected';
        return { success: false, error: errorMessage };
      }

      // Step 3: Submit to upsert endpoint
      debug.trace('[useUsernameSetup] Saving username...');
      const { data, error } = await withTimeout(
        supabase.functions.invoke('creator-profile-upsert', {
          body: {
            wallet_address: walletAddress,
            signature,
            message,
            updates: {
              username: normalized,
              username_lower: normalized,
            },
          },
        }),
        15_000,
        'Save request timed out. Please try again.'
      );

      if (error || data?.error) {
        const errorMsg = data?.error || error?.message || 'Failed to save username';
        console.error('[useUsernameSetup] Save error:', errorMsg);
        
        // Check for duplicate username error
        if (errorMsg.includes('duplicate') || errorMsg.includes('unique') || errorMsg.includes('already exists')) {
          return { success: false, error: 'This username is already taken' };
        }
        
        return { success: false, error: errorMsg };
      }

      // Auto-generate a default bio for new users
      const defaultBio = 'New creator on dextoro Launch 🚀';
      
      // Try to set the default bio (fire and forget, don't block on this)
      supabase.functions.invoke('creator-profile-upsert', {
        body: {
          wallet_address: walletAddress,
          signature,
          message,
          updates: {
            bio: defaultBio,
          },
        },
      }).catch(err => {
        console.warn('[useUsernameSetup] Failed to set default bio:', err);
      });

      // Update local state
      setState(prev => ({
        ...prev,
        needsUsername: false,
        currentUsername: normalized,
        usernameUpdatedAt: new Date().toISOString(),
        showSetupModal: false,
      }));

      return { success: true };
    } catch (err) {
      console.error('[useUsernameSetup] Unexpected error:', err);
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      return { success: false, error: message };
    }
  }, [walletAddress, signMessage, publicKey]);

  return {
    ...state,
    walletAddress,
    saveUsername,
  };
}
